import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthStore } from "@/stores/auth/auth-store";
import {
  useOrganizationStore,
  type OwnerContext,
} from "@/stores/organization/organization-store";
import {
  isRecord,
  parseFederatedViewSourceErrors,
  type CatalogSourceReconnectReason,
  type FederatedDataViewRef,
  type FederatedViewSourceError,
  type QueryTemplateDataViewRef,
} from "@/types/componentDataBinding";
import { queryComponentDataViewRuntime } from "@/services/api/componentSchemaBindingApi";

type ComponentRuntimeDataViewRef = FederatedDataViewRef | QueryTemplateDataViewRef;

interface UseComponentDataViewRuntimeOptions {
  view: ComponentRuntimeDataViewRef | null;
  enabled?: boolean;
  limit?: number;
}

export interface UseComponentDataViewRuntimeResult {
  data: Record<string, unknown>[] | null;
  isLoading: boolean;
  error: string | null;
  sourceErrorCode?: CatalogSourceReconnectReason;
  sourceErrors: FederatedViewSourceError[];
  hasPartialSourceError: boolean;
  refetch: () => Promise<void>;
  lastFetched: Date | null;
}

const DEFAULT_LIMIT = 100;

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getRequestingOrganizationId(
  currentContext: OwnerContext | null,
): string | null {
  if (currentContext?.type !== "organization") {
    return null;
  }

  return getTrimmedString(currentContext.id);
}

function getReconnectReason(
  value: unknown,
): CatalogSourceReconnectReason | null {
  return value === "missing-source-scope" ||
    value === "source-inaccessible" ||
    value === "source-owner-scope-mismatch" ||
    value === "source-permission-denied" ||
    value === "source-write-authority-required" ||
    value === "source-unavailable"
    ? value
    : null;
}

function getErrorDetails(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) {
    return null;
  }

  if (isRecord(error.details)) {
    return error.details;
  }

  if (!isRecord(error.response)) {
    return null;
  }

  const data = error.response.data;
  if (!isRecord(data)) {
    return null;
  }

  return isRecord(data.details) ? data.details : null;
}

function getRuntimeSourceErrorCodeFromDetails(
  details: Record<string, unknown> | null,
): CatalogSourceReconnectReason | null {
  const directReason = getReconnectReason(details?.reconnectReason);
  if (directReason !== null) {
    return directReason;
  }

  if (isRecord(details?.diagnostics)) {
    const diagnosticReason = getReconnectReason(
      details.diagnostics.reconnectReason,
    );
    if (diagnosticReason !== null) {
      return diagnosticReason;
    }
  }

  return null;
}

function getRuntimeResponseSourceErrorCode(
  value: unknown,
  parsedSourceErrors?: FederatedViewSourceError[],
): CatalogSourceReconnectReason | null {
  if (!isRecord(value)) {
    return null;
  }

  const detailsReason = getRuntimeSourceErrorCodeFromDetails(
    isRecord(value.details) ? value.details : null,
  );
  if (detailsReason !== null) {
    return detailsReason;
  }

  if (!isRecord(value.metadata)) {
    return null;
  }

  const metadataReason =
    getReconnectReason(value.metadata.reconnectReason) ??
    getReconnectReason(value.metadata.errorCode);
  if (metadataReason !== null) {
    return metadataReason;
  }

  const sourceErrors =
    parsedSourceErrors ??
    parseFederatedViewSourceErrors(value.metadata.sourceErrors);
  if (sourceErrors.length === 0) {
    return null;
  }

  for (const sourceError of sourceErrors) {
    return sourceError.reconnectReason;
  }

  return null;
}

function getRuntimeResponseSourceErrors(
  value: unknown,
): FederatedViewSourceError[] {
  if (!isRecord(value) || !isRecord(value.metadata)) {
    return [];
  }

  return parseFederatedViewSourceErrors(value.metadata.sourceErrors);
}

function getSourceErrorCode(error: unknown): CatalogSourceReconnectReason {
  const detailsReason = getRuntimeSourceErrorCodeFromDetails(
    getErrorDetails(error),
  );
  if (detailsReason !== null) {
    return detailsReason;
  }

  if (isRecord(error) && error.status === 403) {
    return "source-permission-denied";
  }

  if (isRecord(error) && error.status === 404) {
    return "source-unavailable";
  }

  return "source-unavailable";
}

function getRuntimeViewLabel(view: ComponentRuntimeDataViewRef | null): string {
  return view?.kind === "query-template" ? "Query-template" : "Federated";
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }

  return "DataViewRef runtime query failed.";
}

function hasRequiredViewScope(view: ComponentRuntimeDataViewRef): boolean {
  return (
    getTrimmedString(view.viewId) !== null &&
    getTrimmedString(view.ownerId) !== null &&
    getTrimmedString(view.viewOrganizationId) !== null
  );
}

function getFetchKey(
  view: ComponentRuntimeDataViewRef | null,
  requestingOrganizationId: string | null,
  limit: number,
): string {
  if (view === null) {
    return "data-view:none";
  }

  return [
    view.kind,
    view.viewId,
    view.ownerType,
    view.ownerId,
    view.viewOrganizationId,
    requestingOrganizationId ?? "requester:none",
    String(limit),
  ].join(":");
}

export function useComponentDataViewRuntime({
  view,
  enabled = true,
  limit = DEFAULT_LIMIT,
}: UseComponentDataViewRuntimeOptions): UseComponentDataViewRuntimeResult {
  const [data, setData] = useState<Record<string, unknown>[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceErrorCode, setSourceErrorCode] = useState<
    CatalogSourceReconnectReason | undefined
  >();
  const [sourceErrors, setSourceErrors] = useState<FederatedViewSourceError[]>(
    [],
  );
  const [hasPartialSourceError, setHasPartialSourceError] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const isMountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const authInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const currentContext = useOrganizationStore((state) => state.currentContext);
  const authReady =
    authInitialized &&
    isAuthenticated &&
    typeof accessToken === "string" &&
    accessToken.length > 0;

  const requestingOrganizationId = getRequestingOrganizationId(currentContext);
  const fetchKey = useMemo(
    () => getFetchKey(view, requestingOrganizationId, limit),
    [limit, requestingOrganizationId, view],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeQuery = useCallback(
    async (force = false) => {
      if (!enabled || view === null) {
        setData(null);
        setIsLoading(false);
        setError(null);
        setSourceErrorCode(undefined);
        setSourceErrors([]);
        setHasPartialSourceError(false);
        return;
      }

      if (!hasRequiredViewScope(view)) {
        setData(null);
        setIsLoading(false);
        setError(
          `${getRuntimeViewLabel(view)} DataViewRef is missing view scope and requires reconnection.`,
        );
        setSourceErrorCode("missing-source-scope");
        setSourceErrors([]);
        setHasPartialSourceError(false);
        return;
      }

      if (requestingOrganizationId === null) {
        setData(null);
        setIsLoading(false);
        setError(
          `${getRuntimeViewLabel(view)} DataViewRef runtime requires an authenticated organization context.`,
        );
        setSourceErrorCode("missing-source-scope");
        setSourceErrors([]);
        setHasPartialSourceError(false);
        return;
      }

      if (!authReady) {
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      if (!force && lastFetchedKeyRef.current === fetchKey) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      setSourceErrorCode(undefined);
      setSourceErrors([]);
      setHasPartialSourceError(false);

      try {
        const response = await queryComponentDataViewRuntime({
          view,
          requestingOrganizationId,
          limit,
        });
        if (!isMountedRef.current) {
          return;
        }

        lastFetchedKeyRef.current = fetchKey;
        setData(response.data);
        setLastFetched(new Date());
        const runtimeSourceErrors = getRuntimeResponseSourceErrors(response);
        setSourceErrors(runtimeSourceErrors);

        if (response.success === false) {
          setError(
            response.error ?? "DataViewRef runtime query failed.",
          );
          setSourceErrorCode(
            getRuntimeResponseSourceErrorCode(response, runtimeSourceErrors) ??
              "source-unavailable",
          );
          setHasPartialSourceError(false);
          return;
        }

        const partialSourceErrorCode = getRuntimeResponseSourceErrorCode(
          response,
          runtimeSourceErrors,
        );
        setSourceErrorCode(partialSourceErrorCode ?? undefined);
        setHasPartialSourceError(partialSourceErrorCode !== null);
        setError(
          response.error ??
            (partialSourceErrorCode !== null
              ? `${getRuntimeViewLabel(view)} DataViewRef source requires reconnection.`
              : null),
        );
      } catch (queryError: unknown) {
        if (!isMountedRef.current) {
          return;
        }

        setData(null);
        setError(getErrorMessage(queryError));
        setSourceErrorCode(getSourceErrorCode(queryError));
        setSourceErrors([]);
        setHasPartialSourceError(false);
      } finally {
        isFetchingRef.current = false;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [authReady, enabled, fetchKey, limit, requestingOrganizationId, view],
  );

  useEffect(() => {
    void executeQuery(false);
  }, [executeQuery]);

  const refetch = useCallback(async () => {
    await executeQuery(true);
  }, [executeQuery]);

  return {
    data,
    isLoading,
    error,
    sourceErrorCode,
    sourceErrors,
    hasPartialSourceError,
    refetch,
    lastFetched,
  };
}
